import { Component } from '@theme/component';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { getViewParameterValue } from '@theme/utilities';

/**
 * @param {HTMLFieldSetElement} fieldset
 * @returns {string}
 */
function fieldsetOptionName(fieldset) {
  const legend = fieldset.querySelector('legend');
  if (!legend) return '';

  for (const node of legend.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim() ?? '';
      return text.replace(/:\s*$/, '').trim();
    }
  }

  return '';
}

/**
 * @param {HTMLElement | null} picker
 */
function getVariantSelections(/** @type {HTMLElement | null} */ picker) {
  if (!picker) return [];

  /** @type {HTMLFieldSetElement[]} */
  const fieldsets = Array.from(picker.querySelectorAll('fieldset'));

  return fieldsets
    .map((fs) => {
      const legend = fieldsetOptionName(fs);
      const input = fs.querySelector('input:checked');

      if (!legend || !input?.value) return null;

      return { legend, value: input.value };
    })
    .filter((x) => x !== null);
}

/**
 * @param {HTMLElement} picker
 * @param {{ legend: string, value: string }[]} selections
 * @returns {boolean} Whether a change event was dispatched
 */
function applyVariantSelections(picker, selections) {
  if (!selections.length) return false;

  /** @type {HTMLFieldSetElement[]} */
  const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
  /** @type {HTMLInputElement | null} */
  let lastInput = null;

  for (const { legend, value } of selections) {
    const legendLower = legend.toLowerCase();
    const fieldset = fieldsets.find((fs) => {
      const name = fieldsetOptionName(fs).toLowerCase();
      return name === legendLower || name.startsWith(legendLower) || legendLower.startsWith(name);
    });

    if (!fieldset) continue;

    let input = /** @type {HTMLInputElement | null} */ (null);
    for (const candidate of fieldset.querySelectorAll('input[type="radio"]')) {
      if (candidate instanceof HTMLInputElement && candidate.value === value) {
        input = candidate;
        break;
      }
    }

    if (input instanceof HTMLInputElement && input.getAttribute('aria-disabled') !== 'true') {
      input.checked = true;
      lastInput = input;
    }
  }

  if (lastInput) {
    lastInput.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  return false;
}

/**
 * After a colour switch, replay size/option radios and wait for Horizon's variant picker.
 *
 * @param {HTMLElement} sectionEl
 * @param {HTMLElement | null | undefined} newPicker
 * @param {{ legend: string, value: string }[]} selections
 */
function syncVariantSelectionsAfterMorph(sectionEl, newPicker, selections) {
  if (!(newPicker instanceof HTMLElement) || !selections.length) return Promise.resolve();

  return new Promise((resolve) => {
    let done = false;

    /** @returns {void} */
    function finish() {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      sectionEl.removeEventListener(ThemeEvents.variantUpdate, onUpdate);
      resolve(undefined);
    }

    const timer = window.setTimeout(() => finish(), 4000);

    function onUpdate() {
      finish();
    }

    sectionEl.addEventListener(ThemeEvents.variantUpdate, onUpdate, { once: true });

    const didTrigger = applyVariantSelections(newPicker, selections);
    if (!didTrigger) finish();
  });
}

export default class ColourVariantPicker extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('button[data-product-url]');
    if (!(button instanceof HTMLButtonElement)) return;

    const rawUrl = button.dataset.productUrl?.trim();
    if (!rawUrl) return;

    let targetAbsolute;
    try {
      targetAbsolute = new URL(rawUrl, window.location.origin);
    } catch {
      return;
    }

    /** Pathname of the PDP product currently rendered (section morph does not update window.location). */
    let renderedPathname = window.location.pathname;
    const currentProductUrl = this.dataset.currentProductUrl?.trim();
    if (currentProductUrl) {
      try {
        renderedPathname = new URL(currentProductUrl, window.location.origin).pathname;
      } catch {
        /* keep window.location.pathname */
      }
    }

    if (renderedPathname === targetAbsolute.pathname) return;

    const mainSection = this.closest('.shopify-section');
    if (!(mainSection instanceof HTMLElement) || !mainSection.id) {
      console.warn('[colour-variant-picker] Missing product section');
      return;
    }

    /** @type {HTMLElement | undefined} */
    const recRoot = /** @type {HTMLElement | undefined} */ (
      document.querySelector('[data-testid="product-recommendations-section"]') ??
      document.querySelector('product-recommendations')
    );
    const recSection = /** @type {HTMLElement | undefined} */ (recRoot?.closest('.shopify-section'));

    const picker = /** @type {HTMLElement | null} */ (mainSection.querySelector('variant-picker'));
    const selections = getVariantSelections(picker);

    event.preventDefault();

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const mainUrl = new URL(targetAbsolute.pathname, window.location.origin);
    const viewParam = getViewParameterValue();
    if (viewParam) mainUrl.searchParams.set('view', viewParam);

    /** `section.id`: template--…__main (no shopify-section- prefix) */
    const productSectionApiId = normalizeSectionId(mainSection.id);
    const recSectionApiId = recSection?.id ? normalizeSectionId(recSection.id) : '';

    void (async () => {
      try {
        await sectionRenderer.renderSection(productSectionApiId, { url: mainUrl, cache: false });

        if (signal.aborted) return;

        const newPickerEl = mainSection.querySelector('variant-picker');
        await syncVariantSelectionsAfterMorph(mainSection, newPickerEl, selections);

        if (signal.aborted) return;

        if (recSectionApiId && recSection?.id) {
          await sectionRenderer.renderSection(recSectionApiId, { url: mainUrl, cache: false });
        }

        if (signal.aborted) return;

        history.replaceState({}, '', `${mainUrl.pathname}${mainUrl.search}`);

        document.dispatchEvent(
          new CustomEvent('colour-variant:change', {
            bubbles: true,
            detail: { url: `${window.location.pathname}${window.location.search}` },
          })
        );
      } catch (e) {
        if (!(e instanceof Error) || e.name !== 'AbortError') {
          console.error('[colour-variant-picker]', e);
        }
      }
    })();
  };
}

if (!customElements.get('colour-variant-picker')) {
  customElements.define('colour-variant-picker', ColourVariantPicker);
}
